import {
  LEGAL_MODAL_TRANSLATION_KEY_PREFIXES,
  REPORTS_TRANSLATION_KEY_PREFIXES,
} from '@/utils/i18n/globalTranslationKeyPrefixes';
import type { ChunkDefinition, I18nChunkId } from '@/i18n/types';

/** Dialog-owned hub keys — excluded from hubPage so the two chunks do not collide. */
const HUB_DIALOGS_KEY_PREFIXES = [
  'hub.significantDatesDialog.',
  'hub.cornerstoneIndexDialog.',
  'hub.editResourceDialog.',
  'hub.iconPickerDialog.',
  'hub.exclude.significantDatesDialog.',
  'hub.exclude.iconPickerDialog.',
] as const;

const TRAVEL_AUTH_SHARED_KEY_PREFIX = 'travelAuth.breadcrumb.';
const TRAVEL_POLICY_KEY_PREFIX = 'travelAuth.policy.';
const AUTHORIZATION_FLOW_KEY_PREFIX = 'travelAuth.authorizationFlow.';

export const CHROME_CHUNK_IDS: readonly I18nChunkId[] = [
  'chromeNav',
  'chromeLegal',
  'chromeReports',
] as const;

export const CHUNK_DEFINITIONS: readonly ChunkDefinition[] = [
  {
    id: 'chromeNav',
    relativePath: 'chrome/nav.json',
    surface: 'nav',
    keyPrefixes: ['nav.'],
  },
  {
    id: 'chromeLegal',
    relativePath: 'chrome/legal.json',
    surface: 'legal',
    keyPrefixes: [...LEGAL_MODAL_TRANSLATION_KEY_PREFIXES],
  },
  {
    id: 'chromeReports',
    relativePath: 'chrome/reports.json',
    surface: 'reports',
    keyPrefixes: [...REPORTS_TRANSLATION_KEY_PREFIXES],
  },
  {
    id: 'sharedCommon',
    relativePath: 'shared/common.json',
    surface: 'common',
    keyPrefixes: ['common.'],
    excludedPrefixes: [
      ...LEGAL_MODAL_TRANSLATION_KEY_PREFIXES,
      ...REPORTS_TRANSLATION_KEY_PREFIXES,
    ],
  },
  {
    id: 'hubPage',
    relativePath: 'pages/hub/page.json',
    surface: 'hub',
    keyPrefixes: ['hub.'],
    excludedPrefixes: [...HUB_DIALOGS_KEY_PREFIXES],
  },
  {
    id: 'hubDialogs',
    relativePath: 'pages/hub/dialogs.json',
    surface: 'hubDialogs',
    keyPrefixes: [...HUB_DIALOGS_KEY_PREFIXES],
  },
  {
    id: 'profilePage',
    relativePath: 'pages/profile/page.json',
    surface: 'profile',
    keyPrefixes: ['profile.', 'permissions.', 'perm.', 'languages.'],
  },
  {
    id: 'travelAuthShared',
    relativePath: 'pages/travel-authorization/shared.json',
    surface: 'travelAuthShared',
    keyPrefixes: [TRAVEL_AUTH_SHARED_KEY_PREFIX],
  },
  {
    id: 'travelPolicyPage',
    relativePath: 'pages/travel-authorization/policy.json',
    surface: 'travelPolicy',
    keyPrefixes: [TRAVEL_POLICY_KEY_PREFIX],
  },
  {
    id: 'authorizationFlowPage',
    relativePath: 'pages/travel-authorization/authorization-flow.json',
    surface: 'authorizationFlow',
    keyPrefixes: [AUTHORIZATION_FLOW_KEY_PREFIX],
  },
  {
    id: 'travelAuthRequests',
    relativePath: 'pages/travel-authorization/requests.json',
    surface: 'travelAuth',
    keyPrefixes: ['travelAuth.'],
    excludedPrefixes: [
      TRAVEL_AUTH_SHARED_KEY_PREFIX,
      TRAVEL_POLICY_KEY_PREFIX,
      AUTHORIZATION_FLOW_KEY_PREFIX,
    ],
  },
  {
    id: 'adminBroadcasts',
    relativePath: 'pages/administration/broadcasts.json',
    surface: 'administrationBroadcasts',
    keyPrefixes: ['administration.broadcasts.'],
  },
] as const;

const CHUNK_DEFINITION_BY_ID = new Map<I18nChunkId, ChunkDefinition>(
  CHUNK_DEFINITIONS.map((definition) => [definition.id, definition])
);

export function getChunkDefinition(chunkId: I18nChunkId): ChunkDefinition {
  const definition = CHUNK_DEFINITION_BY_ID.get(chunkId);
  if (!definition) {
    throw new Error(`Unknown i18n chunk id: ${chunkId}`);
  }
  return definition;
}

export function getChunkPath(chunkId: I18nChunkId): string {
  return getChunkDefinition(chunkId).relativePath;
}

export function keyMatchesChunk(key: string, chunkId: I18nChunkId): boolean {
  const definition = getChunkDefinition(chunkId);
  const matchesPrefix = definition.keyPrefixes.some((prefix) =>
    key.startsWith(prefix)
  );
  if (!matchesPrefix) {
    return false;
  }
  if (!definition.excludedPrefixes?.length) {
    return true;
  }
  return !definition.excludedPrefixes.some((prefix) => key.startsWith(prefix));
}

/**
 * Assign a translation key to exactly one chunk.
 * Relies on CHUNK_DEFINITIONS prefix/exclusion rules (including hub page vs dialogs).
 */
export function resolveChunkIdForKey(key: string): I18nChunkId | null {
  for (const definition of CHUNK_DEFINITIONS) {
    if (keyMatchesChunk(key, definition.id)) {
      return definition.id;
    }
  }
  return null;
}
